import { useMemo, useState } from 'react';
import { Box, Button, Flex, IconButton, Input, InputGroup, InputRightElement, Text } from '@chakra-ui/react';
import { FiCheck, FiChevronDown, FiX } from 'react-icons/fi';

const norm = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

/**
 * Categoría con autocompletado: solo las activas del libro (Bancos o Flujo de caja) que sirven para
 * el tipo (ingreso, egreso o «ambos»). La lista se abre debajo del campo y empuja el formulario.
 */
export default function CategoryAutocomplete({ id, categories, book, type, value, onChange, onCreate, allowEmpty = true }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const options = useMemo(
    () => (categories || []).filter((c) => c.book === book && c.active && (c.kind === 'ambos' || !type || c.kind === type)),
    [categories, book, type]
  );
  const selected = options.find((c) => c.id === value) || (categories || []).find((c) => c.id === value) || null;
  const filtered = options.filter((c) => norm(c.name).includes(norm(q)));

  const pick = (c) => {
    onChange(c ? c.id : null);
    setQ('');
    setOpen(false);
  };

  return (
    <Box>
      <InputGroup>
        <Input
          id={id}
          role="combobox"
          aria-expanded={open}
          autoComplete="off"
          placeholder={selected ? '' : 'Escribe para buscar'}
          value={open ? q : selected?.name || ''}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          pr="72px"
        />
        <InputRightElement width="72px" justifyContent="flex-end" pr={1}>
          {allowEmpty && selected && !open && (
            <IconButton aria-label="Quitar categoría" icon={<FiX />} size="xs" variant="ghost" onClick={() => pick(null)} />
          )}
          <IconButton aria-label="Mostrar categorías" icon={<FiChevronDown />} size="xs" variant="ghost" onClick={() => setOpen(!open)} />
        </InputRightElement>
      </InputGroup>
      {open && (
        <Box mt={1} border="1px solid" borderColor="gray.200" borderRadius="md" boxShadow="md" bg="white" overflow="hidden">
          <Box maxH="180px" overflowY="auto" py={1} role="listbox">
            {filtered.map((c) => (
              <Flex
                as="button"
                type="button"
                key={c.id}
                role="option"
                aria-selected={c.id === value}
                onClick={() => pick(c)}
                w="100%"
                justify="space-between"
                align="center"
                px={3.5}
                py={2}
                fontSize="15px"
                textAlign="left"
                bg={c.id === value ? 'purple.50' : 'white'}
                _hover={{ bg: 'gray.50' }}
              >
                {c.name}
                {c.id === value && <FiCheck color="#574feb" />}
              </Flex>
            ))}
            {filtered.length === 0 && (
              <Text px={3.5} py={2.5} fontSize="sm" color="gray.500">
                {options.length ? 'No hay una categoría con ese nombre.' : 'Aún no hay categorías en esta vista.'}
              </Text>
            )}
          </Box>
          {onCreate && (
            <Button variant="link" size="sm" colorScheme="purple" w="100%" justifyContent="flex-start" px={3.5} py={2.5} borderTop="1px solid" borderColor="gray.100" borderRadius={0} onClick={() => { setOpen(false); onCreate(q.trim(), type); }}>
              {q.trim() ? `+ Crear la categoría «${q.trim()}»` : '+ Crear categoría'}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}
